import { ColDef, GridOptions, GridReadyEvent, InitialGroupOrderComparatorParams } from '@ag-grid-community/core';
import { Component, ElementRef, Optional, TemplateRef, ViewChild, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  ActiveStatusEnum,
  Addon,
  BarcodePrintStatusEnum,
  ItemDetail,
  ItemDetailStatusEnum,
  ProductTypeAlt,
} from 'app/model/entities/entity-model';
import { JoDetail } from 'app/model/entities/jo-detail';
import { AddonUtilsService } from 'app/shared/addon-utils.service';
import { AgFns, GridOptionsExt } from 'app/shared/ag-fns';
import { BarcodeService } from 'app/shared/barcode.service';
import { DomainBaseComponent, wrapBusy } from 'app/shared/domain-base.component';
import { DomainService } from 'app/shared/domain.service';
import { PullMatchFns, ReplacementMap } from 'app/shared/pull-match-fns';
import _ from 'lodash';
import { takeUntil } from 'rxjs/operators';
import { ProcessManagerComponent } from './process-manager.component';

@Component({
  selector: 'app-jo-pull-apply',
  templateUrl: './jo-pull-apply.component.html',

  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class JoPullApplyComponent extends DomainBaseComponent {
  @ViewChild('confirmButton') confirmButton!: ElementRef;
  @ViewChild('selectItemDetailCell') selectItemDetailCell!: TemplateRef<any>;

  joPullBatchId!: number;
  joDetailId!: number;
  joDetail!: JoDetail;
  accountAddons!: Addon[];

  shouldPrintBarcodes: boolean = true;

  joDetails!: JoDetail[];
  jodGridOptions!: GridOptionsExt;

  matchingItemDetails!: ItemDetail[];
  itdMatchGridOptions!: GridOptionsExt;
  aggFuncs!: any[];

  itemDetailId!: string;
  itemDetails!: ItemDetail[];
  itdGridOptions!: GridOptionsExt;

  isStyleSizeMismatch!: boolean;
  replacementMap: ReplacementMap = new Map();

  isValidAlternative!: boolean;
  alternativeAlreadyExists!: boolean;

  allowNonAccountOwned = false;
  hasAccountOwnedMatches = false;

  errorMessage = '';

  constructor(
    protected domainService: DomainService,
    protected route: ActivatedRoute,
    protected addonUtilsService: AddonUtilsService,
    protected barcodeService: BarcodeService,
    @Optional() private processManager: ProcessManagerComponent,
  ) {
    super(domainService);

    this.route.paramMap.pipe(takeUntil(this.onDestroy)).subscribe(() => {
      this.updateFromContext();
    });

    // Subscribe to parent's button clicks
    this.processManager?.confirmSelectionClicked$.pipe(takeUntil(this.onDestroy)).subscribe(() => {
      this.confirmThenFocus();
    });
    this.processManager?.shouldPrintBarcodesApplyChanged$.pipe(takeUntil(this.onDestroy)).subscribe((value) => {
      this.shouldPrintBarcodes = value;
    });

    // Subscribe to scanner results from the manager sidebar
    this.processManager?.joPullApplyScannerResult$.pipe(takeUntil(this.onDestroy)).subscribe((result) => {
      const itd = result as ItemDetail;
      if (itd) {
        this.processItemDetailFromScan(itd);
      }
    });

    // Show the buttons and scanner when this page is active
    if (this.processManager) {
      setTimeout(() => {
        this.processManager.showJoPullApplyButtons = true;
        this.processManager.showJoPullApplyScanner = true;
        this.processManager.joPullApplyScannerSearchFn = this.scannerSearch.bind(this);
        this.updateManagerButtonState();
      });
    }
  }

  override ngOnDestroy() {
    if (this.processManager) {
      this.processManager.showJoPullApplyButtons = false;
      this.processManager.canConfirmSelection = false;
      this.processManager.showJoPullApplyScanner = false;
    }
    super.ngOnDestroy();
  }

  private updateManagerButtonState() {
    if (this.processManager) {
      setTimeout(() => {
        this.processManager.canConfirmSelection = this.itemDetails != null && this.errorMessage.length === 0;
        this.processManager.shouldPrintBarcodesApply = this.shouldPrintBarcodes;
      });
    }
  }

  async updateFromContext() {
    this.joPullBatchId = +this.route.snapshot.params['joPullBatchId'];
    this.joDetailId = +this.route.snapshot.params['joDetailId'];
    this.shouldPrintBarcodes = this.route.snapshot.params['shouldPrintBarcodes'] == 'true';

    // Load joDetail before grid init so onJodGridReady can check isAccountOwnedPull
    this.joDetail = <any>await this.dbQueryService.getJoDetailById(this.joDetailId);
    if (this.joDetail) {
      this.joDetails = [this.joDetail];
    } else {
      // TODO: how do we handle this
    }

    // Check if this specific product type is in the account's inventory program
    this.isAccountOwnedPull = false;
    const joh = this.joDetail?.joHeader;
    if (joh?.allowInventory && joh?.inventoryProgramName) {
      const programProductTypeIds = await this.dbQueryService.getAccountOwnedProductTypeIds(joh.inventoryProgramName);
      this.isAccountOwnedPull = programProductTypeIds.includes(this.joDetail.product?.productTypeId);
    }

    this.jodGridOptions = AgFns.initGridOptions(
      this,
      {
        onGridReady: this.onJodGridReady,
        pagination: true,
      },
      { detailProperty: 'joDetailAddons' },
    );
    this.updateJodMasterDetail(this.jodGridOptions);

    this.itdMatchGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onItdMatchGridReady,
      suppressAggFuncInHeader: true,
      autoGroupColumnDef: {
        headerName: 'Group',
        minWidth: 350,
        width: 350,
        suppressSizeToFit: true,
      },
    });

    this.itdGridOptions = AgFns.initGridOptions(
      this,
      {
        onGridReady: this.onItdGridReady,
      },
      { detailProperty: 'itemDetailAddons' },
    );
    this.updateItdMasterDetail(this.itdGridOptions);

    this.accountAddons = await this.dbQueryService.getAddons(this.joDetail.joHeader.accountId);
    // Before the scanner is ready: a scan of a mapped old item must not read as a style mismatch.
    this.replacementMap = await this.dbQueryService.getReplacementMapIntoStyle(this.joDetail.product.productTypeId);

    this.isPageReady = true;
    // page can be ready before this returns.
    this.matchingItemDetails = <any>await this.getMatchingItemDetails(this.joDetail);

    this.focusScanInput();
  }

  async focusScanInput() {
    await this.UtilFns.wait(1);
    this.processManager?.focusJoPullApplyScanner();
  }

  async scannerSearch(value: string): Promise<ItemDetail | null> {
    return <any>await this.dbQueryService.getItemDetailById(value);
  }

  @wrapBusy
  async processItemDetailFromScan(itd: ItemDetail) {
    this.itemDetailId = itd.id;
    await this.validateAndProcessItemDetail(itd);
  }

  isAccountOwnedPull = false;

  onJodGridReady(event: GridReadyEvent) {
    const colDefs: ColDef[] = [
      ...(this.isAccountOwnedPull
        ? [
            {
              headerName: 'Inventory Program',
              field: 'joHeader.inventoryProgramName',
              ...AgFns.flexWidth(200),
            },
          ]
        : []),
      {
        headerName: 'Manufacturer',
        field: 'product.productType.manufacturer.name',
        ...AgFns.flexWidth(200),
      },
      {
        headerName: 'SKU',
        field: 'product.productType.style',
        // filter: "agTextColumnFilter",
        ...AgFns.flexWidth(200),
      },
      { headerName: 'Features', field: 'product.featureChoicesExtract' },
      {
        headerName: 'Description',
        field: 'product.productType.description',
        // filter: "agTextColumnFilter",
        ...AgFns.flexWidth(400),
      },
      { headerName: 'Order Qty', field: 'orderQty' },
      {
        headerName: 'Applied Qty',
        colId: 'itdQty',
        valueGetter: 'data.itemDetails.length',
        filter: 'agNumberColumnFilter',
      },
    ];

    AgFns.initGrid(event, colDefs);
  }

  updateJodMasterDetail(parentGridOptions: GridOptions) {
    const detailGridOptions: GridOptions = AgFns.createDetailGridOptions();
    detailGridOptions.columnDefs = [
      { headerName: 'Addon', field: 'addon.nameAndLocation' },
      { headerName: 'Addl. Info', field: 'additionalInfo' },
      { headerName: 'Price/Unit', field: 'unitPriceAmt' },
    ];
    AgFns.updateColDefs(detailGridOptions.columnDefs);
    parentGridOptions.detailCellRendererParams = {
      detailGridOptions: detailGridOptions,
      getDetailRowData: (params: any) => {
        const jod = params.data as JoDetail;
        params.successCallback(jod.joDetailAddons);
      },
    };
  }

  onItdMatchGridReady(event: GridReadyEvent) {
    const colDefs: ColDef[] = [
      { field: 'fitness', rowGroup: true, hide: true, sort: 'asc' },
      {
        headerName: 'Manufacturer',
        field: 'product.productType.manufacturer.name',
        aggFunc: 'first',
        enableValue: true,
        ...AgFns.flexWidth(200),
      },
      {
        headerName: 'Description',
        field: 'product.productType.description',
        aggFunc: 'first',
        enableValue: true,
        ...AgFns.flexWidth(200),
      },
      {
        headerName: 'SKU',
        field: 'product.productType.style',
        aggFunc: 'first',
        enableValue: true,
        ...AgFns.flexWidth(150),
      },
      {
        headerName: 'Features',
        field: 'product.featureChoicesExtract',
        rowGroup: true,
        hide: false,
        sort: 'asc',
        ...AgFns.flexWidth(150),
      },
      // { headerName: "J/O Detail Id", field: "joDetailId", ...this.AgFns.fixWidth(120) },
      { headerName: 'Bin', field: 'itemBin.name', ...this.AgFns.fixWidth(120) },
      { headerName: 'Account', field: 'account.accountName', ...this.AgFns.flexWidth(200) },
      ...(this.user.isPullAdmin ? [{ ...AgFns.createCellButtonProps('Mark Found', this.selectItemDetailCell) }] : []),
      {
        headerName: 'Inventory Identifier',
        field: 'id',
        cellClass: 'ag-left-aligned-cell',
      },
    ];

    event.api.addAggFuncs({ first: (values) => _.first(values as any) });
    this.itdMatchGridOptions.initialGroupOrderComparator = (params: InitialGroupOrderComparatorParams) => {
      const cA = _.first(params.nodeA.allLeafChildren);
      const cB = _.first(params.nodeB.allLeafChildren);
      const fitnessA = cA ? cA.data.fitness : 0;
      const fitnessB = cA ? cB!.data.fitness : 0;

      if (fitnessA < fitnessB) {
        return -1;
      } else if (fitnessA > fitnessB) {
        return 1;
      } else {
        return 0;
      }
    };
    const sortModel = [{ colId: 'isAccountOwned', sort: 'desc' as const }];

    // expand 1st level automatically
    this.itdMatchGridOptions.groupDefaultExpanded = 1;

    AgFns.initGrid(event, colDefs, sortModel);

    // Next line needs to appear AFTER initGrid above
    this.itdMatchGridOptions.groupDisplayType = 'custom';
  }

  onItdGridReady(event: GridReadyEvent) {
    const colDefs = [
      {
        headerName: 'Inventory Identifier',
        field: 'id',
        cellRenderer: 'agGroupCellRenderer',
        cellClass: 'ag-left-aligned-cell',
      },
      {
        headerName: 'Manufacturer',
        field: 'product.productType.manufacturer.name',
      },
      { headerName: 'Description', field: 'product.productType.description' },
      { headerName: 'SKU', field: 'product.productType.style' },
      { headerName: 'Features', field: 'product.featureChoicesExtract' },
    ];

    AgFns.initGrid(event, colDefs);
    this.itdGridOptions.apiX!.setGridOption('domLayout', 'autoHeight');
  }

  updateItdMasterDetail(parentGridOptions: GridOptions) {
    const detailGridOptions = AgFns.createDetailGridOptions();
    detailGridOptions.columnDefs = [
      { headerName: 'AddOn', field: 'addon.nameAndLocation' },
      { headerName: 'Addl. Info', field: 'additionalInfo' },
    ];
    AgFns.updateColDefs(detailGridOptions.columnDefs);
    parentGridOptions.detailCellRendererParams = {
      detailGridOptions: detailGridOptions,
      getDetailRowData: (params: any) => {
        const itd = params.data as ItemDetail;
        params.successCallback(itd.itemDetailAddons);
      },
    };
  }

  async getMatchingItemDetails(jod: JoDetail) {
    if (jod == null) {
      return;
    }
    const productId = jod.productId;
    const productTypeId = jod.product.productType.id;
    const firstFeatureChoice = jod.product.getFirstFeatureChoice();
    // evaluate fitness
    // query already eliminates damaged/shipped etc items.
    let r = await this.dbQueryService.getAltItemDetailsForStyle(productTypeId);
    r = r.filter((itd) => itd.accountId == null || (jod.joHeader.allowInventory && jod.joHeader.accountId == itd.accountId));
    r = r.filter((itd) => PullMatchFns.mappedVerdict(this.replacementMap, itd.productId, productId) !== 'mappedElsewhere');

    r.forEach((itd) => {
      let fitness = '3 - Alt. product type match, features not matched';
      if (itd.productId === productId) {
        fitness = '0 - Exact product match';
      } else if (PullMatchFns.mappedVerdict(this.replacementMap, itd.productId, productId) === 'mapped') {
        fitness = '0 - Old style product, mapped to this product';
      } else if (itd.product.productTypeId === productTypeId) {
        fitness = '1 - Product type match, features not matched';
      } else {
        fitness = '2 - Alt. product match';
      }
      itd['fitness'] = fitness;
    });

    // Sort account-owned items first
    this.hasAccountOwnedMatches = jod.joHeader.allowInventory && r.some((itd) => itd.accountId != null);
    if (this.hasAccountOwnedMatches) {
      r = _.orderBy(r, [(itd) => (itd.accountId != null ? 0 : 1), 'fitness']);
    }

    return r;
  }

  // Hit when scanning or typing in itemDetailId
  onInputFilled(event: { target: any }, fn: (value: any) => void) {
    const input = event.target;
    const length = input.value.length;
    const maxLength = input.attributes.maxlength.value;

    if (length >= maxLength) {
      // bind is needed to associate the fn returned with 'this' component.
      fn.bind(this)(input.value);
    } else {
      this.errorMessage = '';
      this.itemDetails = [];
      this.updateManagerButtonState();
    }
  }

  canCheckItemDetail(itemDetail: ItemDetail) {
    return this.user.isPullAdmin && itemDetail?.id && itemDetail.joDetailId == null;
  }

  // Hit when clicking 'Mark as Scanned'
  @wrapBusy
  selectItemDetail(itemDetail: ItemDetail) {
    this.processItemDetailId(itemDetail.id);
  }

  @wrapBusy
  async processItemDetailId(itemDetailId: string) {
    // Just a sanity check - only way this could happen is if the user
    // clicks a 2nd time on another itemDetailId before the screen closes.
    if (this.joDetail.itemDetails.length >= this.joDetail.orderQty) {
      return;
    }

    this.errorMessage = 'Searching...';
    this.updateManagerButtonState();
    const r = await this.dbQueryService.getItemDetailById(itemDetailId);
    this.errorMessage = '';
    this.itemDetails = r ? [r] : [];
    this.updateManagerButtonState();

    this.UtilFns.wait(100);
    AgFns.autoSizeAllColumns(this.itdGridOptions);

    if (r == null) {
      this.errorMessage = 'No matching inventory found';
      this.updateManagerButtonState();
      return;
    }

    await this.validateAndProcessItemDetail(r);
  }

  private async validateAndProcessItemDetail(r: ItemDetail) {
    this.itemDetails = [r];
    this.updateManagerButtonState();

    if (r.joDetailId != null) {
      this.errorMessage = 'This inventory item is reserved for Job Order Detail: ' + r.joDetailId;
      this.updateManagerButtonState();
      return;
    }

    if (this.hasAccountOwnedMatches && !this.allowNonAccountOwned && r.accountId == null) {
      this.errorMessage = "Non-account-owned items are not allowed. Check 'Allow non-Account-Owned items' to override.";
      this.updateManagerButtonState();
      return;
    }

    const fcs = r.product.productFeatureChoices.map((x) => x.featureChoice);
    for (var fc of fcs) {
      if (fc.activeStatusId == ActiveStatusEnum.Inactive) {
        this.errorMessage = `Feature Choice '${fc.choiceValue}' on Feature '${fc.feature.name}' is inactive. Cannot reserve this item.`;
        this.updateManagerButtonState();
        return;
      }
      if (fc.feature.activeStatusId == ActiveStatusEnum.Inactive) {
        this.errorMessage = `Feature '${fc.feature.name}' is inactive. Cannot reserve this item.`;
        this.updateManagerButtonState();
        return;
      }
    }

    const idt = this.itemDetails[0];
    const jod = this.joDetails[0];
    const mapped = PullMatchFns.mappedVerdict(this.replacementMap, idt.productId, jod.productId);
    if (mapped === 'mappedElsewhere') {
      this.errorMessage = PullMatchFns.mappedElsewhereMessage(jod.product.productType.style);
      this.updateManagerButtonState();
      return;
    }
    // An old item mapped to this line's product is an exact match: no alternate style is registered.
    this.isStyleSizeMismatch = idt.productId !== jod.productId && mapped !== 'mapped';

    if (this.isStyleSizeMismatch) {
      if (idt.product.productTypeId !== jod.product.productTypeId) {
        // see if it is already in the Alt table
        this.alternativeAlreadyExists = jod.product.productType.productTypeAlts.some(
          (ss) => ss.productTypeId === jod.product.productTypeId && ss.altProductTypeId === idt.product.productTypeId,
        );
      } else {
        this.alternativeAlreadyExists = true;
      }
      this.isValidAlternative = !this.alternativeAlreadyExists;

      // If alternative is already registered, auto-confirm since no user action is needed
      if (this.alternativeAlreadyExists) {
        await this.confirmThenFocus();
      }
    } else {
      await this.confirmThenFocus();
    }
  }

  // this focusScanInput cannot run until isBusy is false
  async confirmThenFocus() {
    await this.confirmSelection();
    await this.focusScanInput();
  }

  @wrapBusy
  async confirmSelection() {
    const itd = this.itemDetails[0];
    const jod = this.joDetail;
    // sanity check
    if (jod.itemDetails.length >= jod.orderQty) {
      return;
    }

    itd.joDetailId = jod.id;
    // accountPurchaseJoDetailId is intentionally NOT cleared here. It is a historical
    // reference back to the original Proximity JO that placed the account-owned inventory
    // order. It is never used in the forward workflow after PO receipt, and the
    // "Create Account-Owned Vouchers" query won't pick this item up again because
    // accountPurchaseInvoiceHeaderId is already set from the original PO receipt voucher.
    itd.barcodePrintStatusId = BarcodePrintStatusEnum.NotYetPrinted;

    itd.lastScanTs = new Date(Date.now());
    if (this.isStyleSizeMismatch && this.isValidAlternative) {
      const altKey = [jod.product.productTypeId, itd.product.productTypeId];
      if (this.uow.getEntityByKey(ProductTypeAlt, altKey) == null) {
        this.uow.createEntity(ProductTypeAlt, {
          productTypeId: jod.product.productTypeId,
          altProductTypeId: itd.product.productTypeId,
        });
      }
    }
    // Now create ItemDetailAddons to match the JoDetailAddons
    const itdAddons = this.addonUtilsService.createItemDetailAddonsFromJoDetail(jod, itd, this.accountAddons);
    if (itd.itemDetailAddons.length > 0) {
      itd.itemDetailStatusId = ItemDetailStatusEnum.InProcessPendingAddon;
    } else {
      itd.itemDetailStatusId = ItemDetailStatusEnum.InProcessPendingShipPrep;
    }
    const addonBins = await this.addonUtilsService.findOrCreateAddonBins([itd]);

    await this.addonUtilsService.checkAndRemoveEmptyBins(addonBins.map((x) => x.id));

    if (itd.itemDetailStatusId === ItemDetailStatusEnum.InProcessPendingAddon) {
      const unassigned = itd.itemDetailAddons.filter(ida => ida.addonBinId == null);
      if (unassigned.length > 0) {
        throw new Error(`ItemDetail ${itd.id}: ${unassigned.length} of ${itd.itemDetailAddons.length} addon(s) missing addonBinId after findOrCreateAddonBins; aborting save.`);
      }
    }

    await this.dbSaveService.saveChanges();
    this.itemDetailId = '';

    if (this.shouldPrintBarcodes) {
      const changes = await this.barcodeService.printItemDetailBarcodesAndNewAddonBinLabels([itd]);
      if (changes.length > 0) {
        await this.dbSaveService.saveSelectedChanges(changes);
      }
    }

    this.jodGridOptions.apiX!.redrawRows();
    this.toastr.success('Item reserved in inventory');
    this.itemDetails = [];
    this.itemDetailId = '';
    this.updateManagerButtonState();

    // refreshes the joDetailId column so that you can see what was just selected.
    this.itdMatchGridOptions.apiX!.refreshCells();

    // check if all units applied
    if (jod.itemDetails.length >= jod.orderQty) {
      this.goBack();
    }
  }
}
